/**
 * @fileoverview Anonymous Account Manager for Firebase-first temporary accounts
 *
 * Handles the flow: new → temp → real account progression
 * Uses Firebase as primary storage with localStorage as backup/cache
 */

import { UserProfile, MMRData } from '../MMR/types';
import { UserDataService } from '../firebase/userDataService';
import { containsInappropriateContent } from '../../utils/profanityFilter';

export type AccountType = 'new' | 'temp' | 'real';

export interface LoginState {
  accountType: AccountType;
  deviceId?: string;
  username?: string;
  lastLoginEmail?: string;
  tempAccountData?: UserProfile;
  canPromote?: boolean;
}

export interface AnonymousAccountData {
  deviceId: string;
  username: string;
  profile: UserProfile;
  gamesPlayed: number;
  createdAt: Date;
  lastActive: Date;
  firebaseUid?: string; // When linked to Firebase anonymous auth
  usernameChanged: boolean; // Track if user has used their one username change
}

export class AnonymousAccountManager {
  private static readonly DEVICE_KEY = 'zodiac_device_id';
  private static readonly LOGIN_STATE_KEY = 'zodiac_login_state';
  private static readonly TEMP_ACCOUNT_KEY = 'zodiac_temp_account';
  private static readonly LOGIN_HISTORY_KEY = 'zodiac_login_history';

  /**
   * Get or create a unique device identifier
   */
  static getOrCreateDeviceId(): string {
    let deviceId = localStorage.getItem(this.DEVICE_KEY);
    if (!deviceId) {
      deviceId = this.generateDeviceId();
      localStorage.setItem(this.DEVICE_KEY, deviceId);
    }
    return deviceId;
  }

  /**
   * Generate a unique device identifier
   */
  private static generateDeviceId(): string {
    // Use crypto.randomUUID if available, fallback to custom generation
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    // Fallback UUID generation
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Check current login state and determine what to do
   */
  static getLoginState(): LoginState {
    const stored = localStorage.getItem(this.LOGIN_STATE_KEY);

    if (!stored) {
      // Brand new user
      return { accountType: 'new' };
    }

    try {
      const loginState: LoginState = JSON.parse(stored);
      return loginState;
    } catch (error) {
      return { accountType: 'new' };
    }
  }

  /**
   * Update login state in localStorage
   */
  static setLoginState(state: LoginState): void {
    localStorage.setItem(this.LOGIN_STATE_KEY, JSON.stringify(state));
  }

  /**
   * Create a new temporary account
   */
  static async createTempAccount(username?: string): Promise<AnonymousAccountData> {
    const deviceId = this.getOrCreateDeviceId();

    // Generate username if not provided
    const finalUsername = username || this.generateTempUsername();

    const now = new Date();

    // Create basic MMR data for temp account
    const mmrData: MMRData = {
      provisionalRating: 1300,
      placementGames: 0,
      placementHistory: [],
      displayRating: 0,
      rankedGames: 0,
      peak: 0,
      isPlaced: false,
      totalGames: 0,
      createdAt: now,
      colorBalance: 0  // Color balancing: +1 for light, -1 for dark (targets 0)
    };

    const profile: UserProfile = {
      mmr: mmrData,
      username: finalUsername,
      lastActive: now
    };

    const tempAccount: AnonymousAccountData = {
      deviceId,
      username: finalUsername,
      profile,
      gamesPlayed: 0,
      createdAt: now,
      lastActive: now,
      usernameChanged: false
    };

    // Store temp account data
    localStorage.setItem(this.TEMP_ACCOUNT_KEY, JSON.stringify(tempAccount));

    // Update login state
    this.setLoginState({
      accountType: 'temp',
      deviceId,
      username: finalUsername,
      tempAccountData: profile,
      canPromote: false
    });

    return tempAccount;
  }

  /**
   * Load existing temp account
   */
  static getTempAccount(): AnonymousAccountData | null {
    const stored = localStorage.getItem(this.TEMP_ACCOUNT_KEY);
    if (!stored) return null;

    try {
      const tempAccount = JSON.parse(stored);

      // Helper to safely convert dates
      const toDate = (value: any): Date | undefined => {
        if (!value) return undefined;
        try {
          const date = new Date(value);
          // Check if valid
          if (isNaN(date.getTime())) return undefined;
          return date;
        } catch {
          return undefined;
        }
      };

      // Convert date strings back to Date objects
      tempAccount.createdAt = new Date(tempAccount.createdAt);
      tempAccount.lastActive = new Date(tempAccount.lastActive);
      tempAccount.profile.lastActive = new Date(tempAccount.profile.lastActive);
      tempAccount.profile.mmr.createdAt = new Date(tempAccount.profile.mmr.createdAt);

      // Convert optional date fields safely
      if (tempAccount.profile.mmr.lastGameDate) {
        tempAccount.profile.mmr.lastGameDate = toDate(tempAccount.profile.mmr.lastGameDate);
      }
      if (tempAccount.profile.lastUsernameChange) {
        tempAccount.profile.lastUsernameChange = toDate(tempAccount.profile.lastUsernameChange);
      }
      if (tempAccount.profile.lastAvatarChange) {
        tempAccount.profile.lastAvatarChange = toDate(tempAccount.profile.lastAvatarChange);
      }

      // Convert dates in placement history
      if (tempAccount.profile.mmr.placementHistory && Array.isArray(tempAccount.profile.mmr.placementHistory)) {
        tempAccount.profile.mmr.placementHistory = tempAccount.profile.mmr.placementHistory.map((game: any) => ({
          ...game,
          gameDate: toDate(game.gameDate) || new Date()
        }));
      }

      return tempAccount;
    } catch (error) {
      return null;
    }
  }

  /**
   * Update temp account data - now Firebase-first with localStorage cache
   */
  static async updateTempAccount(profile: UserProfile, firebaseUid?: string): Promise<void> {
    const tempAccount = this.getTempAccount();
    if (!tempAccount) {
      return;
    }

    tempAccount.profile = profile;
    tempAccount.lastActive = new Date();
    tempAccount.gamesPlayed = profile.mmr.totalGames;

    // Update localStorage cache
    localStorage.setItem(this.TEMP_ACCOUNT_KEY, JSON.stringify(tempAccount));

    // CRITICAL: Update Firebase if we have UID (primary storage)
    if (firebaseUid) {
      try {
        await UserDataService.updateUserProfile(firebaseUid, profile);
      } catch (error) {
        // Continue with localStorage update as fallback
      }
    }

    // Check if user is eligible for promotion
    const canPromote = this.shouldPromoteToRealAccount(tempAccount);

    // Update login state
    const currentState = this.getLoginState();
    this.setLoginState({
      ...currentState,
      tempAccountData: profile,
      canPromote
    });

  }

  /**
   * Check if temp account should be promoted to real account
   */
  static shouldPromoteToRealAccount(account: AnonymousAccountData): boolean {
    const profile = account.profile;
    const currentRating = profile.mmr.isPlaced ? profile.mmr.displayRating : profile.mmr.provisionalRating;

    return (
      profile.mmr.totalGames >= 3 ||
      profile.mmr.placementGames >= 7 ||
      currentRating >= 1400
    );
  }

  /**
   * Generate a random temporary username
   */
  private static generateTempUsername(): string {
    const adjectives = ['Quick', 'Smart', 'Bold', 'Swift', 'Clever', 'Sharp', 'Bright', 'Fast', 'Wise', 'Cool', 'Sea', 'Fire', 'Ice', 'Sky', 'Star', 'Wild', 'Cloud', 'Comet', 'Sun', 'Moon', 'Mountain', 'Earth', 'Stone'];
    const nouns = ['Bear', 'Crane', 'Frog', 'Fungi', 'Goat', 'Monkey', 'Octopus', 'Penguin', 'Rhino', 'Snake', 'Tree', 'Wolf', 'Virus', 'King'];

    const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    const number = Math.floor(Math.random() * 9999) + 1;

    return `${adjective}${noun}${number}`;
  }

  /**
   * Remember that user logged in with real account
   */
  static rememberRealAccountLogin(email: string): void {
    this.setLoginState({
      accountType: 'real',
      lastLoginEmail: email
    });

    // Add to login history
    this.addToLoginHistory(email);

  }

  /**
   * Add email to login history for returning user recognition
   */
  private static addToLoginHistory(email: string): void {
    const historyKey = this.LOGIN_HISTORY_KEY;
    const existing = localStorage.getItem(historyKey);

    let history: { emails: string[], mostUsed?: string } = { emails: [] };

    if (existing) {
      try {
        history = JSON.parse(existing);
      } catch (error) {
      }
    }

    // Add email if not already present
    if (!history.emails.includes(email)) {
      history.emails.push(email);
    }

    // Update most used (simple: last used becomes most used)
    history.mostUsed = email;

    localStorage.setItem(historyKey, JSON.stringify(history));
  }

  /**
   * Get returning user suggestion
   */
  static getReturningUserSuggestion(): string | null {
    const history = localStorage.getItem(this.LOGIN_HISTORY_KEY);
    if (!history) return null;

    try {
      const parsed = JSON.parse(history);
      return parsed.mostUsed || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Clear temp account data (when promoted to real account)
   */
  static clearTempAccount(): void {
    localStorage.removeItem(this.TEMP_ACCOUNT_KEY);
  }

  /**
   * Change username for temp account (allowed once)
   */
  static async changeTempAccountUsername(newUsername: string): Promise<{ success: boolean; error?: string }> {
    const tempAccount = this.getTempAccount();
    if (!tempAccount) {
      return { success: false, error: 'No temp account found' };
    }

    if (tempAccount.usernameChanged) {
      return { success: false, error: 'Username can only be changed once' };
    }

    // Validate username
    const validation = this.validateUsername(newUsername);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    // Update temp account
    tempAccount.username = newUsername;
    tempAccount.profile.username = newUsername;
    tempAccount.usernameChanged = true;
    tempAccount.lastActive = new Date();

    // Save to localStorage
    localStorage.setItem(this.TEMP_ACCOUNT_KEY, JSON.stringify(tempAccount));

    // Update login state
    const loginState = this.getLoginState();
    loginState.username = newUsername;
    if (loginState.tempAccountData) {
      loginState.tempAccountData.username = newUsername;
    }
    this.setLoginState(loginState);

    return { success: true };
  }

  /**
   * Check if temp account can change username
   */
  static canChangeTempUsername(): { canChange: boolean; reason?: string } {
    const tempAccount = this.getTempAccount();
    if (!tempAccount) {
      return { canChange: false, reason: 'No temp account found' };
    }

    if (tempAccount.usernameChanged) {
      return { canChange: false, reason: 'Username already changed once' };
    }

    return { canChange: true };
  }

  /**
   * Validate username using bad-words library
   */
  private static validateUsername(username: string): { valid: boolean; error?: string } {
    if (!username || username.trim().length === 0) {
      return { valid: false, error: 'Username cannot be empty' };
    }

    const trimmed = username.trim();

    if (trimmed.length < 3) {
      return { valid: false, error: 'Username must be at least 3 characters' };
    }

    if (trimmed.length > 20) {
      return { valid: false, error: 'Username must be 20 characters or less' };
    }

    // Check for valid characters (letters, numbers, underscores, hyphens)
    const validPattern = /^[a-zA-Z0-9_-]+$/;
    if (!validPattern.test(trimmed)) {
      return { valid: false, error: 'Username can only contain letters, numbers, underscores, and hyphens' };
    }

    // Professional profanity and inappropriate content check
    if (containsInappropriateContent(trimmed)) {
      return { valid: false, error: 'Username contains inappropriate content' };
    }

    return { valid: true };
  }

  /**
   * Get temp account info for UI display
   */
  static getTempAccountInfo(): {
    username: string;
    canChangeUsername: boolean;
    gamesPlayed: number;
    rating: number;
    canPromote: boolean;
  } | null {
    const tempAccount = this.getTempAccount();
    if (!tempAccount) return null;

    const currentRating = tempAccount.profile.mmr.isPlaced
      ? tempAccount.profile.mmr.displayRating
      : tempAccount.profile.mmr.provisionalRating;

    return {
      username: tempAccount.username,
      canChangeUsername: !tempAccount.usernameChanged,
      gamesPlayed: tempAccount.gamesPlayed,
      rating: currentRating,
      canPromote: this.shouldPromoteToRealAccount(tempAccount)
    };
  }

  /**
   * Reset all data (for testing or complete logout)
   */
  static resetAll(): void {
    localStorage.removeItem(this.DEVICE_KEY);
    localStorage.removeItem(this.LOGIN_STATE_KEY);
    localStorage.removeItem(this.TEMP_ACCOUNT_KEY);
    localStorage.removeItem(this.LOGIN_HISTORY_KEY);
  }
}